/**
 * InertWorkshopChip — a calendar chip for a workshop that isn't the viewer's
 * own. Built from the same `WorkshopsCalendarChipCompact` /
 * `WorkshopsCalendarChipFull` pieces as the normal chip, so it collapses and
 * expands on a dense Month day the same way, but it can never be opened: no
 * click, no lift, no hover shadow, a muted colour, and a tooltip naming whose
 * workshop it is.
 */
import { useId } from 'react';
import type { ReactElement } from 'react';

import { Tooltip } from '../../../shared/Tooltip';

import { useWorkshopChipHover } from './use-workshop-chip-hover';
import { inertChipColors } from './workshops-calendar-chip-shared';
import { fmtTime } from './workshops-calendar-dates';
import { WorkshopsCalendarChipCompact } from './WorkshopsCalendarChipCompact';
import { WorkshopsCalendarChipFull } from './WorkshopsCalendarChipFull';
import type { WorkshopChip } from './workshopsCalendarTypes';

export interface InertWorkshopChipProps {
  workshop: WorkshopChip;
  view: 'Month' | 'Week';
  /** Dense Month days collapse to the compact row until hovered. */
  compact: boolean;
}

/** "Ray's workshop — view only", or a plain fallback when the workshop has no
 *  lead teacher to name. */
function tooltipText(workshop: WorkshopChip): string {
  return workshop.leadInstructor
    ? `${workshop.leadInstructor}'s workshop — view only`
    : 'View only';
}

export function InertWorkshopChip({
  workshop,
  view,
  compact,
}: InertWorkshopChipProps): ReactElement {
  const { hovered, onMouseEnter, onMouseLeave } = useWorkshopChipHover();
  const tooltipId = useId();
  const timeLabel = fmtTime(workshop.localDate ?? workshop.date);
  const shared = {
    w: workshop,
    timeLabel,
    colors: inertChipColors(workshop),
    clickable: false,
    onMouseEnter,
    onMouseLeave,
  };

  return (
    <Tooltip content={tooltipText(workshop)} id={tooltipId} className="block w-full">
      {/* Focusable (not clickable) so a keyboard user can reach the tooltip —
          nothing else about the chip is in the tab order. The opacity mutes
          the fill and the text in one step. */}
      <div tabIndex={0} aria-describedby={tooltipId} style={{ opacity: 0.72, cursor: 'default' }}>
        {compact && !hovered ? (
          <WorkshopsCalendarChipCompact {...shared} />
        ) : (
          <WorkshopsCalendarChipFull {...shared} view={view} hovered={false} />
        )}
      </div>
    </Tooltip>
  );
}
